<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import NavigationButtons from "@/entries/interact-form/components/NavigationButtons.vue";
import ProgressBar from "@/entries/interact-form/components/ProgressBar.vue";
import QuestionDisplay from "@/entries/interact-form/components/QuestionDisplay.vue";
import { useFormDataStore } from "@/entries/interact-form/stores/formData";

const props = defineProps<{
	contactformHtml: string;
	contactformId: string;
	isOpen: boolean;
}>();

const emit = defineEmits<{
	(e: "close"): void;
	(e: "updateAnswer", id: number, answer: string | string[]): void;
	(e: "lastQuestionSubmit"): void;
}>();

const formDataStore = useFormDataStore();

// 送信中フラグ（二重送信防止）
const isSubmitting = ref(false);

// CF7失敗系イベントのハンドラ（リスナー登録・解除で同一参照を使用）
const handleFormFailure = () => {
	isSubmitting.value = false;
};

/**
 * 回答更新のハンドラ
 *
 * 質問への回答が更新されたときに親コンポーネントに通知し、
 * select_buttonタイプの場合は自動的に次の質問に進みます
 *
 * @param {string | string[]} answer - 更新する回答（文字列または文字列の配列）
 * @param {boolean} [isSelectButton=false] - 選択ボタンタイプかどうか
 */
function handleAnswerUpdate(
	answer: string | string[],
	isSelectButton: boolean = false,
) {
	const questionId = formDataStore.currentQuestion?.id || 0;

	emit("updateAnswer", questionId, answer);

	// select_buttonタイプの場合は、回答選択時に自動的に次へ進む
	if (isSelectButton) {
		formDataStore.nextQuestion();
	}
}

/**
 * フォーム送信ハンドラ
 *
 * Contact Form 7のフォームを送信します
 * 送信中は再入ガードで複数送信を防止
 */
function handleFormSubmit() {
	// 二重送信防止：送信中なら早期終了
	if (isSubmitting.value) {
		return;
	}

	// 特定のフォームをIDで取得
	let form = document.getElementById(
		`interact-contact-form-${props.contactformId}`,
	) as HTMLFormElement;

	// IDで見つからない場合は、モーダル内のフォームを取得
	if (!form) {
		const modal = document.querySelector(".modal-container");
		if (modal) {
			form = modal.querySelector(".wpcf7 form") as HTMLFormElement;
		}
	}

	if (form) {
		// Contact Form 7の送信ボタンを取得
		const submitButton = form.querySelector(
			".wpcf7-submit",
		) as HTMLInputElement;
		if (submitButton) {
			// 送信中フラグを立てる（実際にCF7送信に進む段階）
			isSubmitting.value = true;

			// 送信ボタンが非表示になっている可能性があるため、一時的に表示
			const originalDisplay = submitButton.style.display;
			submitButton.style.display = "block";
			submitButton.click();

			// 元の表示状態に戻す
			setTimeout(() => {
				submitButton.style.display = originalDisplay;
			}, 100);
		} else {
			console.error("[FormModal] Contact Form 7 submit button not found");
			// 送信ボタンが見つからない場合は、フォームを直接送信
			// 送信中フラグを立てる
			isSubmitting.value = true;
			const submitEvent = new Event("submit", {
				bubbles: true,
				cancelable: true,
			});
			form.dispatchEvent(submitEvent);
		}
	} else {
		console.error(
			"[FormModal] Contact Form 7 form not found for ID:",
			props.contactformId,
		);
	}
}

/**
 * CF7失敗系イベントのリスナー登録
 * 送信中フラグをリセットして再送信可能な状態にする
 */
onMounted(() => {
	document.addEventListener("wpcf7invalid", handleFormFailure);
	document.addEventListener("wpcf7spam", handleFormFailure);
	document.addEventListener("wpcf7mailfailed", handleFormFailure);
});

/**
 * CF7イベントリスナーの解除（メモリリーク防止）
 */
onUnmounted(() => {
	document.removeEventListener("wpcf7invalid", handleFormFailure);
	document.removeEventListener("wpcf7spam", handleFormFailure);
	document.removeEventListener("wpcf7mailfailed", handleFormFailure);
});
</script>

<template>
  <template v-if="isOpen">
    <!-- モーダルオーバーレイ -->
    <div class="modal-overlay" @click="emit('close')"></div>

    <!-- モーダルコンテンツ -->
    <div class="modal-container">
      <div class="interact-form modal-content">
        <div class="modal-header">
          <button @click="emit('close')" class="close-button">×</button>
        </div>

        <div v-if="formDataStore.totalQuestions > 0" class="modal-body">
          <!-- 進行状況バー（全ての質問 + Contact Form） -->
          <ProgressBar :current="formDataStore.hasCompletedAllQuestions
            ? formDataStore.totalQuestions
            : formDataStore.currentQuestionIndex
            " :total="formDataStore.totalQuestions" 
            :isContactForm="formDataStore.hasCompletedAllQuestions" />

          <!-- 全ての質問に回答した場合はContact Formを表示 -->
          <div v-if="formDataStore.hasCompletedAllQuestions" class="contact-form-container">
            <!-- コンシェルジュ画像とメッセージ -->
            <div class="concierge-message-container">
              <img src="@/entries/interact-form/img/concierge.png" alt="コンシェルジュ"
                class="concierge-image" />
              <div class="concierge-message">
                <p>ご回答ありがとうございました。</p>
                <p>あなたにあったサービスをご紹介しますので、</p>
                <p>ご連絡先を教えてください。</p>
              </div>
            </div>

            <!-- 送信・戻るボタン（固定エリア） -->
            <div class="fixed-buttons-area">
              <div class="submit-button-wrapper">
                <button type="button" class="back-to-questions-button"
                  @click.prevent.stop="formDataStore.prevQuestion">
                  質問に戻る
                </button>
                <button type="button" class="submit-button" :disabled="isSubmitting" @click="handleFormSubmit">
                  送信
                </button>
              </div>
            </div>

            <!-- Contact Form 7のHTMLを表示（スクロール領域のみ） -->
            <div class="contact-form-content">
              <slot name="contact-form"></slot>
            </div>
          </div>
          <!-- まだ質問が残っている場合は質問を表示 -->
          <div v-else class="question-container">
            <div class="question-scroll-area">
              <QuestionDisplay :question="formDataStore.currentQuestion" :isModal="true"
                @update:answer="handleAnswerUpdate" />
            </div>

            <!-- ナビゲーションボタン -->
            <div class="navigation-buttons-wrapper">
              <NavigationButtons :is-first-question="formDataStore.currentQuestionIndex === 0"
                :is-last-question="formDataStore.currentQuestionIndex === formDataStore.totalQuestions - 1
                  " :is-next-disabled="formDataStore.isNextButtonDisabled"
                @prev="formDataStore.prevQuestion" @next="formDataStore.nextQuestion"
                @submit="emit('lastQuestionSubmit')" />
            </div>
          </div>
        </div>

        <div v-else class="no-questions">
          <p>質問が読み込まれていないか、有効な質問がありません。</p>
        </div>
      </div>
    </div>
  </template>
</template>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
}

.modal-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1001;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  border-radius: 26px;
  background-color: #eaf9fa;
}

.modal-content {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  padding: 20px;
  position: relative;
}

.modal-header {
  position: absolute;
  top: 12px;
  right: 12px;
}

.close-button {
  background: none;
  border: none;
  font-size: 42px;
  font-weight: 100;
  cursor: pointer;
  color: #a5dbdf;
}

.interact-form {
  background-color: transparent;
  padding: 20px;
  border-radius: 8px;
}

.no-questions {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  color: #666;
  margin: 20px 0;
}

.modal-body {
  display: flex;
  flex-direction: column;
  max-height: calc(90vh - 60px);
  overflow: hidden;
}

.question-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: calc(90vh - 150px);
}

.question-scroll-area {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 20px;
  min-height: 200px;
  max-height: calc(90vh - 250px);

  scrollbar-width: thin;
  scrollbar-color: #a5dbdf transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #a5dbdf;
    border-radius: 4px;
  }
}

.navigation-buttons-wrapper {
  margin-top: auto;
  padding-top: 15px;
  position: sticky;
  bottom: 0;
  background-color: #eaf9fa;
  z-index: 2;
  width: 100%;
}

.contact-form-container {
  padding: 0;
  display: flex;
  flex-direction: column;
  max-height: calc(90vh - 120px);
  overflow: hidden;
}

.concierge-message-container {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-shrink: 0;
  padding: 16px 16px 0;
}

.concierge-image {
  width: 50px;
  height: auto;
  margin-right: 16px;
}

.concierge-message {
  border-radius: 16px;
  border: 2px solid #36b4a8;
  font-size: 14px;
  font-weight: 700;
  padding: 8px 12px;
  background-color: #fff;
  flex-grow: 1;

  p {
    font-size: 14px;
    font-weight: 700;
    margin: 0;
    line-height: 1.4;
  }
}

.fixed-buttons-area {
  flex-shrink: 0;
  padding: 8px 16px;
  background-color: #eaf9fa;
  margin-bottom: 8px;
}

.submit-button-wrapper {
  display: flex;
  justify-content: space-around;
  align-items: center;

  .back-to-questions-button {
    padding: 8px 16px;
    font-size: 20px;
    width: 45%;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    background-color: transparent;
    color: #a5dbdf;

    &:hover {
      color: #36b4a8;
    }
  }

  .submit-button {
    box-sizing: border-box;
    color: white;
    outline: 2px solid #fff;
    box-shadow: none;
    outline-offset: -3px;
    padding: 8px 16px;
    font-size: 20px;
    border: none;
    border-radius: 12px;
    background-color: #36b4a8;
    width: 45%;
    cursor: pointer;

    &:hover {
      background-color: #2ca09a;
    }

    &:active {
      background-color: #259189;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;

      &:hover {
        background-color: #36b4a8;
      }

      &:active {
        background-color: #36b4a8;
      }
    }

    &.inactive {
      opacity: 0.5 !important;
      cursor: not-allowed !important;
    }
  }
}

.contact-form-content {
  margin-top: 0;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 16px 20px;

  scrollbar-width: thin;
  scrollbar-color: #333333 transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #333333;
    border-radius: 4px;
  }

  // 固定height(35px)がpadding+font-sizeと衝突し、初期値テキストが上下に見切れるため、
  // 高さをコンテンツに合わせて自然に決まるよう上書き
  :deep(.wpcf7) select {
    height: auto;
    padding: 8px 10px;
    line-height: 1.4;
  }
}

@media screen and (max-width: 767px) {
  .modal-body {
    max-height: calc(95vh - 60px);
  }

  .question-container {
    max-height: calc(95vh - 120px);
  }

  .question-scroll-area {
    max-height: calc(95vh - 200px);
    min-height: 150px;

    &::-webkit-scrollbar {
      width: 4px;
    }
  }

  .navigation-buttons-wrapper {
    padding-top: 10px;
  }

  .modal-container {
    width: 95%;
    max-height: 95vh;
    border-radius: 16px;
  }

  .modal-content {
    padding: 15px;
  }

  .close-button {
    font-size: 28px;
    top: 8px;
    right: 8px;
  }

  .concierge-message-container {
    position: relative;
    margin-bottom: 6px;
    padding: 8px 8px 0;
  }

  .concierge-image {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 40px;
    z-index: 1;
  }

  .concierge-message {
    padding: 6px 8px 6px 56px;
    border-radius: 12px;
    border-width: 1px;
    margin-left: 0;

    p {
      font-size: 12px;
      line-height: 1.3;
    }
  }

  .fixed-buttons-area {
    padding: 6px 8px;
    margin-bottom: 6px;
  }

  .submit-button-wrapper {
    flex-direction: column-reverse;
    gap: 8px;

    .back-to-questions-button {
      padding: 6px 12px;
      font-size: 16px;
      height: 32px;
      border-radius: 16px;
    }

    .submit-button {
      padding: 6px 12px;
      font-size: 16px;
      border-radius: 16px;
      height: 32px;
      outline-offset: -2px;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;

        &:hover {
          background-color: #36b4a8;
        }

        &:active {
          background-color: #36b4a8;
        }
      }
    }
  }

  .contact-form-container {
    padding: 0;
    max-height: calc(95vh - 90px);
  }

  .contact-form-content {
    margin-top: 0;
    padding: 0 8px 16px;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #333333;
    }
  }

  .modal-body {
    font-size: 14px;
  }
}
</style>
